import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  notification,
  Row,
  Space,
  Table,
  Typography,
} from 'antd';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CaseFileDownloadLink from '../../components/core/File/CaseFileDownloadLink';
import BreadCrumbs from '../../components/core/BreadCrumbs';
import { formatDate } from '../../utils/date';
import BasicCard from '../../components/core/Cards/BasicCard';
import InfoItem from '../../components/core/Cards/InfoItem';
import BasicDivider from '../../components/core/Divider/BasicDivider';
import { APP_TERMS } from '../../app-terms';
import { useGetCase } from '../../hooks/cases/useGetCase';
import { useUserLocale } from '../../hooks/useLocale';
import {
  getPatientDetailInfoItems,
  getStudyPurposeInfoItems,
  mapAntFileToServerFile,
  mapCaseToUpload,
} from '../../utils/case';
import UploadFiles from './Forms/UploadFiles';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../axios/apiService';
import { getSearchParamFromLocation } from '../../utils/mapper';
import { AxiosError } from 'axios';
import { BAD_REQUEST_STATUS_CODE } from '../../axios/constants';
import CaseReportTemplateDownloader from '../../components/core/File/CaseReportTemplateDownloader';

const { Text } = Typography;
const { TextArea } = Input;

const EditFormLayout = ({
  caseId,
  initialValues,
  uploadFilesForm,
  loading,
  isUploading,
  breadCrumbs,
  commentsForm,
  onFormSubmit,
  onMarkAsSubmitted,
  onUploadingChange,
}) => {
  const { userLocale } = useUserLocale();
  const navigate = useNavigate();
  const patientDetails = useMemo(
    () => getPatientDetailInfoItems(initialValues, userLocale),
    [initialValues]
  );
  const studyPurpose = useMemo(
    () => getStudyPurposeInfoItems(initialValues),
    [initialValues]
  );
  const files = useMemo(() => initialValues?.files, [initialValues]);

  const FILES_COLUMNS = useMemo(
    () => [
      {
        title: 'File Name',
        dataIndex: 'fileName',
        align: 'left',
        render: (value, record) => {
          return (
            <CaseFileDownloadLink
              caseId={initialValues?.caseId}
              fileName={value}
              objectKey={record?.objectKey}
            />
          );
        },
        onCell: () => {
          return { colSpan: 2 };
        },
        colSpan: 2,
      },
      {
        title: 'Size',
        dataIndex: 'fileSize',
        align: 'right',
        ellipsis: true,
        render: (value) => {
          const KB = parseInt(value / 1024) || '-';
          return <Text style={{ textWrap: 'wrap' }}>{KB} KB</Text>;
        },
      },
    ],
    [initialValues]
  );

  const validateForm = async () => {
    try {
      await uploadFilesForm?.validateFields();
      await commentsForm?.validateFields();
      return true;
    } catch (error) {
      console.error(
        'Errors : ',
        error?.errorFields?.flatMap((e) => e.errors)?.join('\n')
      );
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    isValid &&
      onFormSubmit?.({
        files: uploadFilesForm?.getFieldValue('files'),
        internalComments: commentsForm?.getFieldValue('internalComments'),
      });
  };

  const handleMarkAsSubmitted = async () => {
    const isValid = await validateForm();
    isValid &&
      onMarkAsSubmitted({
        files: uploadFilesForm?.getFieldValue('files'),
        internalComments: commentsForm?.getFieldValue('internalComments'),
      });
  };

  return (
    <>
      <BreadCrumbs items={breadCrumbs} />
      <div className="row card">
        <div className="card invoices-tabs-card">
          <div className="card-body card-body pt-0 pb-0">
            <div className="invoices-main-tabs">
              <div className="row align-items-center">
                <div className="wizard mt-4">
                  <Row justify={'space-between'}>
                    <Col xs={24} sm={24} md={8} className="text-start">
                      <b>Case ID: </b>
                      {initialValues?.caseId}
                    </Col>
                    <Col xs={24} sm={24} md={8} className="text-center">
                      <b>Date: </b>
                      {initialValues?.createdAt &&
                        formatDate(
                          initialValues?.createdAt,
                          APP_TERMS.date_1_format,
                          userLocale
                        )}
                    </Col>
                    <Col xs={24} sm={24} md={8} className="text-end">
                      <b>Patient Name: </b>
                      {`${initialValues?.patient?.firstName} ${initialValues?.patient?.lastName}`}
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
            <Row gutter={[16, 16]} className="mt-3">
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.generic_patients} ${APP_TERMS.generic_details}`}
                >
                  {patientDetails?.map((details, index) => {
                    return (
                      <Fragment key={details?.label}>
                        <InfoItem
                          label={details?.label}
                          value={details?.value}
                        />
                        {index < patientDetails?.length - 1 ? (
                          <BasicDivider />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </BasicCard>
              </Col>
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.generic_study} ${APP_TERMS.generic_purpose}`}
                >
                  {studyPurpose?.map((details, index) => {
                    return (
                      <Fragment key={details?.label}>
                        <InfoItem
                          label={details?.label}
                          value={details?.value}
                          type={'vertical'}
                        />
                        {index < studyPurpose?.length - 1 ? (
                          <BasicDivider />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </BasicCard>
              </Col>
              <Col sm={24} md={12}>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={APP_TERMS.generic_files}
                >
                  <Table
                    columns={FILES_COLUMNS}
                    dataSource={files}
                    pagination={false}
                    rowKey={(record) => record?.fileId}
                  />
                </BasicCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]} className="mt-3">
              <Col span={24}>
                <Flex justify="end" className="mb-2">
                  <CaseReportTemplateDownloader caseId={caseId}/>
                </Flex>
                <BasicCard
                  size="small"
                  style={{ height: '100%' }}
                  title={`${APP_TERMS.process_case_title}`}
                >
                  <UploadFiles form={uploadFilesForm} onUploadingChange={onUploadingChange} />
                  <Row>
                    <Col span={24}>
                      <Form
                        layout="vertical"
                        name="comments"
                        form={commentsForm}
                      >
                        <Form.Item
                          label={
                            <Text>
                              {APP_TERMS.generic_comments} -{' '}
                              <Text type="danger">Only for admin</Text>
                            </Text>
                          }
                          name="internalComments"
                        >
                          <TextArea rows={4} placeholder={''} />
                        </Form.Item>
                      </Form>
                    </Col>
                  </Row>
                </BasicCard>
              </Col>
            </Row>
            <Space className="mt-3">
              <Button
                htmlType="button"
                disabled={loading || isUploading}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <Button
                disabled={loading || isUploading}
                loading={loading === 'saving'}
                type="primary"
                htmlType="button"
                onClick={handleSubmit}
              >
                Save As Draft
              </Button>
              <Button
                disabled={loading || isUploading}
                loading={loading === 'submitting'}
                type="primary"
                htmlType="button"
                onClick={handleMarkAsSubmitted}
              >
                Submit Case
              </Button>
            </Space>
          </div>
        </div>
      </div>
    </>
  );
};

EditFormLayout.propTypes = {
  caseId: PropTypes.string,
  initialValues: PropTypes.any,
  loading: PropTypes.bool,
  isUploading: PropTypes.bool,
  breadCrumbs: PropTypes.array,
  uploadFilesForm: PropTypes.any,
  commentsForm: PropTypes.any,
  onFormSubmit: PropTypes.func,
  onMarkAsSubmitted: PropTypes.func,
  onUploadingChange: PropTypes.func,
};

const BREAD_CRUMBS = [
  { label: 'Case', to: '/user/case/created' },
  { label: 'Process', to: '/user/case/edit' },
];
const EditCase = () => {
  const { data } = useGetCase();
  const [loading, setLoading] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [commentsForm] = Form.useForm();
  const [uploadFilesForm] = Form.useForm();
  const [api, contentHolder] = notification.useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!data) return;

    // Map Case details to input form values
    const initialUploads = mapCaseToUpload(data);
    const internalComments = data?.internalComments;

    // initialize forms with case input data
    initialUploads && uploadFilesForm.setFieldsValue(initialUploads);
    internalComments && commentsForm.setFieldsValue({ internalComments });
  }, [data]);

  const caseId = useMemo(
    () => getSearchParamFromLocation('id', location),
    [location]
  );

  const handleSubmit = async (data, _loading = 'saving') => {
    const payload = {
      ...data,
      files: data?.files?.map(mapAntFileToServerFile),
    };
    setLoading(_loading);
    try {
      const id = getSearchParamFromLocation('id', location);
      const response = await apiService.updateCase(id, payload);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      navigate('/user/case/new');
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error?.response?.status === BAD_REQUEST_STATUS_CODE &&
        !error?.response?.data?.formErrorMessage
      )
        return;
      api.error({
        message: 'Error',
        description: error.response.data?.error || error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(undefined);
    }
  };

  const handleMarkAsSubmitted = async (data) => {
    return handleSubmit(
      {
        ...(data || {}),
        markAsSubmitted: true,
      },
      'submitting'
    );
  };

  return (
    <>
      {contentHolder}
      <Form.Provider>
        <EditFormLayout
          caseId={caseId}
          uploadFilesForm={uploadFilesForm}
          commentsForm={commentsForm}
          loading={loading}
          isUploading={isUploading}
          initialValues={data}
          breadCrumbs={BREAD_CRUMBS}
          onFormSubmit={handleSubmit}
          onMarkAsSubmitted={handleMarkAsSubmitted}
          onUploadingChange={setIsUploading}
        />
      </Form.Provider>
    </>
  );
};

export default EditCase;
